/**
 * M27 task-07 — new LLM model form (ADM-01/02; HUB-082). Hand-rolled form with
 * a direct hub call (db-connectors/Create precedent). The API key is a
 * WRITE-ONLY password field, sent once on create and never rendered from any
 * response. The base-URL field is shown for the providers that cannot be
 * constructed without one (`azure`, `openai-compatible`) — the shared
 * `requiresBaseUrl` rule the write route enforces. The first model created
 * becomes the hub default server-side (§4 D-3).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { HubClientError } from '@verybigthings/trackframe-hub-client';
import {
  requiresBaseUrl,
  type LlmModel,
  type LlmModelCreate,
} from '@verybigthings/trackframe-shared/schemas';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SecretField } from '@/components/SecretField';
import { BASE_URL_HINTS, PROVIDERS, ProviderSelect } from './provider-select';
import { hub } from '@/lib/hub-client';

export function ModelsCreate() {
  const navigate = useNavigate();
  const [internalName, setInternalName] = useState('');
  const [provider, setProvider] = useState<(typeof PROVIDERS)[number]>('openai');
  const [modelName, setModelName] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const body: LlmModelCreate = {
        internalName: internalName.trim(),
        provider,
        modelName: modelName.trim(),
        apiKey,
        ...(requiresBaseUrl(provider) && baseUrl.trim() ? { baseUrl: baseUrl.trim() } : {}),
        ...(isDefault ? { isDefault: true } : {}),
      };
      const row: LlmModel = await hub.llmModelCreate({ body });
      navigate(`/admin/models/edit/${row.id}`);
    } catch (err) {
      setError(err instanceof HubClientError ? err.message : (err as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">New model</h1>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Model</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            <div className="space-y-2">
              <Label htmlFor="internalName">Name</Label>
              <Input
                id="internalName"
                placeholder="gpt-5.6-luna-main"
                value={internalName}
                onChange={(e) => setInternalName(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">
                Internal label used to bind this model to a purpose.
              </p>
            </div>
            <ProviderSelect value={provider} onChange={setProvider} />
            <div className="space-y-2">
              <Label htmlFor="modelName">Model name</Label>
              <Input
                id="modelName"
                placeholder="gpt-5.6-luna"
                value={modelName}
                onChange={(e) => setModelName(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">The provider&apos;s model identifier.</p>
            </div>
            {requiresBaseUrl(provider) && (
              <div className="space-y-2">
                <Label htmlFor="baseUrl">{BASE_URL_HINTS[provider]?.label ?? 'Base URL'}</Label>
                <Input
                  id="baseUrl"
                  placeholder={BASE_URL_HINTS[provider]?.placeholder}
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">{BASE_URL_HINTS[provider]?.hint}</p>
              </div>
            )}
            <SecretField
              id="apiKey"
              label="API key"
              hasSecret={false}
              value={apiKey}
              onChange={setApiKey}
              required
            />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
              Make this the hub default model
            </label>
            {error && <Alert>{error}</Alert>}
            <div className="flex gap-2">
              <Button type="submit" disabled={busy}>
                {busy ? 'Creating…' : 'Create model'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/admin/models')}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
